import React, { useState } from 'react';
import {
  Paper,
  Typography,
  Box,
  Chip,
  Avatar,
  AvatarGroup,
  Rating,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Divider,
  Alert,
  IconButton,
  Tooltip,
  Popover
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PersonIcon from '@mui/icons-material/Person';
import StarIcon from '@mui/icons-material/Star';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { DataAsset } from '../../types/DataAsset';
import api from '../../services/api';

interface AtAGlanceSidebarProps {
  asset: DataAsset;
  onRatingChange?: (assetId: string) => void;
}

/**
 * AtAGlanceSidebar Component
 * Displays key metadata in a sidebar (Customer Analytics style)
 */
const AtAGlanceSidebar: React.FC<AtAGlanceSidebarProps> = ({ asset, onRatingChange }) => {
  const [ratingDialogOpen, setRatingDialogOpen] = useState(false);
  const [newRating, setNewRating] = useState<number | null>(0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [infoAnchorEl, setInfoAnchorEl] = useState<HTMLElement | null>(null);

  const handleInfoClick = (event: React.MouseEvent<HTMLElement>) => {
    setInfoAnchorEl(event.currentTarget);
  };

  const handleInfoClose = () => {
    setInfoAnchorEl(null);
  };

  const infoOpen = Boolean(infoAnchorEl);

  const handleSubmitRating = async () => {
    if (!newRating || newRating < 1) return;

    try {
      setSubmitting(true);
      setError(null);
      
      await api.post(`/data-assets/${asset._id}/rating`, { rating: newRating });

      setRatingDialogOpen(false);
      setNewRating(0);
      if (onRatingChange) {
        onRatingChange(asset._id);
      }
    } catch (err: any) {
      console.error('Error submitting rating:', err);
      const errorMessage = err.response?.data?.error || err.message || 'Failed to submit rating';
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <>
      <Paper sx={{ p: 3, position: 'sticky', top: 20 }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
          At a glance
        </Typography>

        <Divider sx={{ my: 2 }} />

        {/* Page Visits */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <VisibilityIcon fontSize="small" color="action" />
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
              Page Visits
            </Typography>
          </Box>
          <Typography variant="h5" sx={{ fontWeight: 600, color: 'primary.main' }}>
            {asset.pageVisits?.toLocaleString() || 0}
          </Typography>
        </Box>

        {/* Last Modified */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <CalendarTodayIcon fontSize="small" color="action" />
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
              Last modified
            </Typography>
          </Box>
          <Typography variant="body2">{formatDate(asset.lastModified)}</Typography>
        </Box>

        {/* Owner */}
        {(asset.ownerId || asset.owner) && (
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <PersonIcon fontSize="small" color="action" />
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                Owner
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main', fontSize: '14px' }}>
                {asset.ownerId?.name
                  ? getInitials(asset.ownerId.name)
                  : getInitials(asset.owner)}
              </Avatar>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {asset.ownerId?.name || asset.owner}
                </Typography>
                {asset.ownerId?.email && (
                  <Typography variant="caption" color="text.secondary">
                    {asset.ownerId.email}
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
        )}

        {/* Subject Matter Experts */}
        {asset.subjectMatterExperts && asset.subjectMatterExperts.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 1 }}>
              Subject Matter Expert
            </Typography>
            <AvatarGroup max={3}>
              {asset.subjectMatterExperts.map((expert) => (
                <Avatar
                  key={expert._id}
                  sx={{ width: 32, height: 32, bgcolor: '#005a9c', fontSize: '14px' }}
                  title={expert.name}
                >
                  {getInitials(expert.name)}
                </Avatar>
              ))}
            </AvatarGroup>
          </Box>
        )}

        {/* Rating */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <StarIcon fontSize="small" color="action" />
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
              Rating
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Rating
              value={asset.rating?.average || 0}
              precision={0.5}
              readOnly
              size="small"
            />
            <Typography variant="body2" color="text.secondary">
              ({asset.rating?.count || 0})
            </Typography>
          </Box>
          <Button
            size="small"
            variant="outlined"
            onClick={() => setRatingDialogOpen(true)}
            sx={{ mt: 1 }}
          >
            Rate this asset
          </Button>
        </Box>

        {/* Tags */}
        {asset.tags && asset.tags.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 1 }}>
              Tags
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {asset.tags.map((tag) => (
                <Chip
                  key={tag}
                  label={tag}
                  size="small"
                  sx={{
                    bgcolor: '#e8f0fe',
                    color: 'primary.main',
                    fontWeight: 600,
                    fontSize: '11px',
                  }}
                />
              ))}
            </Box>
          </Box>
        )}

        {/* Privacy Classification */}
        {asset.privacyClassification && (
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                Privacy Classification
              </Typography>
              <Tooltip title="Click to see privacy classification levels and colors">
                <IconButton
                  size="small"
                  onClick={handleInfoClick}
                  sx={{
                    p: 0.25,
                    color: 'primary.main',
                    '&:hover': { bgcolor: 'rgba(0, 51, 102, 0.08)' }
                  }}
                  aria-label="More information about privacy classification"
                >
                  <InfoOutlinedIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
            </Box>
            <Chip
              label={asset.privacyClassification}
              size="small"
              color={asset.privacyClassification === 'Public' ? 'success' : 'warning'}
            />
          </Box>
        )}

        {/* Compliance Flags */}
        {asset.complianceFlags && asset.complianceFlags.length > 0 && (
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 1 }}>
              Compliance
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {asset.complianceFlags.map((flag) => (
                <Chip
                  key={flag}
                  label={flag}
                  size="small"
                  sx={{
                    bgcolor: '#fff3e0',
                    color: '#e65100',
                    fontWeight: 600,
                    fontSize: '11px',
                  }}
                />
              ))}
            </Box>
          </Box>
        )}
      </Paper>

      {/* Privacy Classification Info Popover */}
      <Popover
        open={infoOpen}
        anchorEl={infoAnchorEl}
        onClose={handleInfoClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        sx={{
          '& .MuiPopover-paper': {
            maxWidth: 400,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            border: '1px solid #e0e0e0'
          }
        }}
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'primary.main', mb: 1 }}>
            About Privacy Classification
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, lineHeight: 1.6 }}>
            Defines the level of privacy protection required for this data asset based on its content and regulatory requirements.
          </Typography>
          <Typography variant="caption" sx={{ fontWeight: 600, color: 'primary.main', display: 'block', mb: 0.5 }}>
            Levels & Colors:
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.5, '& li': { mb: 0.5 } }}>
            <Typography component="li" variant="caption" color="text.secondary">
              <Box
                component="span"
                sx={{
                  display: 'inline-block',
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  bgcolor: '#4caf50',
                  mr: 0.5,
                  verticalAlign: 'middle',
                  border: '1px solid #ddd'
                }}
              />
              <strong>Public:</strong> Publicly accessible information
            </Typography>
            <Typography component="li" variant="caption" color="text.secondary">
              <Box
                component="span"
                sx={{
                  display: 'inline-block',
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  bgcolor: '#ff9800',
                  mr: 0.5,
                  verticalAlign: 'middle',
                  border: '1px solid #ddd'
                }}
              />
              <strong>Internal:</strong> Internal use only, not for external sharing
            </Typography>
            <Typography component="li" variant="caption" color="text.secondary">
              <Box
                component="span"
                sx={{
                  display: 'inline-block',
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  bgcolor: '#f57c00',
                  mr: 0.5,
                  verticalAlign: 'middle',
                  border: '1px solid #ddd'
                }}
              />
              <strong>Confidential:</strong> Restricted access, sensitive business data
            </Typography>
            <Typography component="li" variant="caption" color="text.secondary">
              <Box
                component="span"
                sx={{
                  display: 'inline-block',
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  bgcolor: '#d32f2f',
                  mr: 0.5,
                  verticalAlign: 'middle',
                  border: '1px solid #ddd'
                }}
              />
              <strong>Restricted:</strong> Highly restricted, requires special authorization
            </Typography>
          </Box>
        </Box>
      </Popover>

      {/* Rating Dialog */}
      <Dialog 
        open={ratingDialogOpen} 
        onClose={() => {
          setRatingDialogOpen(false);
          setError(null);
          setNewRating(0);
        }}
        maxWidth="sm"
        fullWidth
        aria-labelledby="rating-dialog-title"
      >
        <DialogTitle 
          id="rating-dialog-title"
          sx={{ 
            bgcolor: 'primary.main', 
            color: 'white',
            fontFamily: 'Merriweather, serif',
            fontWeight: 700
          }}
        >
          Rate this Asset
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {error && (
            <Alert 
              severity="error" 
              sx={{ mb: 2 }}
              onClose={() => setError(null)}
            >
              {error}
            </Alert>
          )}
          <Box sx={{ py: 2, textAlign: 'center' }}>
            <Typography variant="body1" gutterBottom sx={{ fontWeight: 500 }}>
              How would you rate {asset.name}?
            </Typography>
            <Rating
              value={newRating}
              onChange={(event, newValue) => {
                setNewRating(newValue);
                setError(null);
              }}
              size="large"
              sx={{ 
                mt: 2,
                '& .MuiRating-iconFilled': {
                  color: '#FFB400'
                },
                '& .MuiRating-iconHover': {
                  color: '#FFB400'
                }
              }}
              aria-label="Rate this asset"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={() => {
              setRatingDialogOpen(false);
              setError(null);
              setNewRating(0);
            }}
            sx={{ 
              color: '#666',
              textTransform: 'none',
              '&:focus': {
                outline: '2px solid #003366',
                outlineOffset: '2px'
              }
            }}
            aria-label="Cancel rating"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmitRating}
            variant="contained"
            disabled={!newRating || newRating < 1 || submitting}
            sx={{
              bgcolor: 'primary.main',
              color: 'white',
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': {
                bgcolor: '#005a9c'
              },
              '&:focus': {
                outline: '2px solid #003366',
                outlineOffset: '2px'
              },
              '&:disabled': {
                bgcolor: '#ccc'
              }
            }}
            aria-label="Submit rating"
          >
            {submitting ? 'Submitting...' : 'Submit'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AtAGlanceSidebar;
